import { useMemo, useState } from 'react';
import dayJs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { appConfig } from '@app/app';
import { Tooltip } from '@app/ui/tooltip';
import { Loader } from '@app/ui/loader';
import { Label } from '@app/ui/label';
import { error as toastError } from '@app/snackbars';

import {
  RULES_FAMILY_SUMMARY_GET_MANY,
  RULE_SET_VERSION_CREATE_DRAFT,
  RULE_SET_VERSION_GET_MANY,
  RULE_SET_VERSION_GET_ONE,
} from '../gql';
import { RuleFamily, RuleSetStatus, RuleSetVersion, RulesFamilySummary } from '../types';
import { DRY_RUN_SUPPORTED_FAMILIES, FAMILY_SLUGS, NEW_RULE_SEGMENT } from '../constants';
import { canPublishFamily, familyRuleCount, getGraphQLErrorMessage, refetchUnlessSkipped } from '../utils';
import { RuleList } from './RuleList';
import { RulesEmptyState } from './RulesEmptyState';
import { VersionHistory } from './VersionHistory';
import { DryRunBench } from './DryRunBench';
import { PublishDialog } from './PublishDialog';
import { DiscardDraftDialog } from './DiscardDraftDialog';
import { ColorRubricPanel } from './color/ColorRubricPanel';
import { MatchingRulesPanel } from './matching/MatchingRulesPanel';

export interface FamilyRulesPanelProps {
  readonly family: RuleFamily;
}

export const FamilyRulesPanel = ({ family }: FamilyRulesPanelProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const dryRunSupported = DRY_RUN_SUPPORTED_FAMILIES.includes(family);

  const [showHistory, setShowHistory] = useState(false);
  const [dryRunTargetVersionId, setDryRunTargetVersionId] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery<{
    rulesFamilySummaryGetMany: RulesFamilySummary[];
  }>(RULES_FAMILY_SUMMARY_GET_MANY, { fetchPolicy: 'cache-and-network' });
  const summary = summaryData?.rulesFamilySummaryGetMany.find(entry => entry.family === family) ?? null;
  // A family with no engine is READ-ONLY here (`[RULED TL-6]`), and this fails CLOSED: only an
  // explicit `editable: true` opens the write affordances, so a failed summary query offers buttons
  // whose every mutation 400s. While it is still in flight neither the buttons nor the notice show.
  const familyEditable = summary?.editable === true;
  const summaryPending = summaryLoading && !summaryData;

  const {
    data: versionsData,
    loading: versionsLoading,
    refetch: refetchVersions,
  } = useQuery<{ ruleSetVersionGetMany: RuleSetVersion[] }>(RULE_SET_VERSION_GET_MANY, {
    variables: { family },
    fetchPolicy: 'cache-and-network',
  });
  // Sorted here, not trusted from the server: a change to the resolver's `orderBy` would otherwise
  // silently make `[0]` the wrong version.
  const versions = useMemo(
    () => [...(versionsData?.ruleSetVersionGetMany ?? [])].sort((a, b) => b.version - a.version),
    [versionsData],
  );
  const activeVersion = versions[0] ?? null;

  const {
    data: activeData,
    loading: activeLoading,
    refetch: refetchActive,
    // `cache-and-network`: the editor is a sibling ROUTE, so coming back from a create/edit
    // remounts this panel and a cache-only read would show the pre-edit list.
  } = useQuery<{ ruleSetVersionGetOne: RuleSetVersion | null }>(RULE_SET_VERSION_GET_ONE, {
    variables: { id: activeVersion?.id },
    skip: !activeVersion,
    fetchPolicy: 'cache-and-network',
  });
  const activeVersionData = activeData?.ruleSetVersionGetOne ?? null;
  const rules = activeVersionData?.rules ?? [];
  // The colour family has no `Rule` rows — `rules.length` is always 0 for it. `familyRuleCount`
  // mirrors the backend's own `FAMILY_CHILDREN[family].count` (scoring + gates for colour, bands
  // deliberately excluded) — used for the discard button and the dialogs' counts; Publish uses `canPublishFamily`.
  const ruleCount = familyRuleCount(family, activeVersionData);
  const canPublish = canPublishFamily(family, activeVersionData);
  const blockedPublishTooltip =
    family === RuleFamily.colorCoding
      ? t('rules.publish.colorIncompleteTooltip')
      : t('rules.publish.emptyDraftTooltip');
  const publishTooltip = canPublish ? t('rules.publish.tooltip') : blockedPublishTooltip;
  // The EMPTY-STATE gate is a separate, wider question ("has this colour version ever been
  // populated at all") — bands alone (no scoring rules, no gates) must still open the editable
  // three-section panel, not the Import prompt, or an admin who deleted every scoring rule/gate
  // (leaving bands) would be stuck: `rulesImportCatalogue` refuses once any colour version exists.
  const hasAnyColorRows =
    (activeVersionData?.colorScoringRules?.length ?? 0) +
      (activeVersionData?.colorGates?.length ?? 0) +
      (activeVersionData?.colorBands?.length ?? 0) >
    0;
  const isEmpty = family === RuleFamily.colorCoding ? !hasAnyColorRows : ruleCount === 0;

  const [createDraft, { loading: creatingDraft }] = useMutation<{ ruleSetVersionCreateDraft: RuleSetVersion }>(
    RULE_SET_VERSION_CREATE_DRAFT,
  );

  const parentVersionId = useMemo(() => {
    if (!activeVersion?.parentVersion) {
      return null;
    }

    return versions.find(version => version.version === activeVersion.parentVersion)?.id ?? null;
  }, [versions, activeVersion]);

  const isDraft = activeVersion?.status === RuleSetStatus.draft;

  // `RULE_SET_VERSION_GET_ONE` is `skip: !activeVersion` above — Apollo still runs a `refetch()` call
  // on a skipped query, with `variables: { id: undefined }`, and the API 400s on it
  // (`Variable "$id" of required type "Int!" was not provided"). Guard with the same condition; once
  // a draft is created, the next render un-skips the query and it fetches on its own.
  const refetchAll = () =>
    Promise.all([refetchSummary(), refetchVersions(), refetchUnlessSkipped(refetchActive, !activeVersion)]);

  const handleStartDraft = async () => {
    try {
      await createDraft({ variables: { family } });
      await refetchAll();
    } catch (e) {
      // The server's own message (e.g. a draft already exists) is the useful one.
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  const handlePublished = async () => {
    setPublishOpen(false);
    await refetchAll();
  };

  const handleDiscarded = async () => {
    setDiscardOpen(false);
    await refetchAll();
  };

  // Create and edit are ONE form: "Add rule" opens the editor empty, not a second dialog.
  const handleAddRule = () =>
    navigate(`/admin/settings/${FAMILY_SLUGS[family]}/rule/${NEW_RULE_SEGMENT}`, {
      state: { fromSearch: location.search },
    });

  const openDryRun = (versionId: number) => setDryRunTargetVersionId(versionId);

  const isLoadingRules = versionsLoading || activeLoading;

  return (
    <Box>
      {/* Square top-left corner so the card sits flush under the tab bar's square bottom edge. */}
      <Paper
        variant="outlined"
        elevation={0}
        sx={{ borderRadius: '0 16px 16px 16px', p: { xs: 2, sm: 3, lg: 4 }, mb: 3 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h1">{t(`rules.family.${family}`)}</Typography>
            <Typography variant="body1" color="text.secondary">
              {summary?.publishedVersion
                ? t('rules.summary.publishedVersion', { version: summary.publishedVersion })
                : t('rules.summary.noPublishedVersion')}
              {summary?.publishedAt &&
                ` — ${t('rules.summary.publishedAt', {
                  date: dayJs(summary.publishedAt).format(appConfig.format.date),
                })}`}
              {summary?.draftVersion && ` — ${t('rules.summary.draftInProgress', { version: summary.draftVersion })}`}
            </Typography>
            {/* The only draft affordance that isn't per-row, so it must stay visible with zero rules. */}
            {isDraft && (
              <Label
                text={t('rules.summary.editingDraftChip', { version: activeVersion?.version })}
                color="warning"
                sx={{ mt: 1, lineHeight: 'normal' }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Tooltip
              title={
                (!versions.length ? t('rules.versionHistory.emptyTooltip') : t('rules.versionHistory.tooltip')) ??
                undefined
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primaryLight"
                  disabled={!versions.length}
                  onClick={() => setShowHistory(previous => !previous)}
                >
                  {t('rules.versionHistory.title')}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                (!dryRunSupported ? t('rules.summary.dryRunDisabledTooltip') : t('rules.summary.dryRunTooltip')) ??
                undefined
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primaryLight"
                  disabled={!dryRunSupported || !activeVersion}
                  onClick={() => activeVersion && openDryRun(activeVersion.id)}
                >
                  {t('rules.summary.dryRunButton')}
                </Button>
              </span>
            </Tooltip>

            {!familyEditable && !summaryPending && (
              <Typography variant="body1" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {t('rules.summary.readOnlyFamilyNotice')}
              </Typography>
            )}

            {familyEditable && isDraft && (
              <>
                {/* Colour draft rules are added from within the three sections below, never from a
                single top-level "Add" navigating to the (watchlist-only) routed `RuleEditor`. */}
                {family === RuleFamily.watchlist && (
                  <Tooltip title={t('rules.create.tooltip') ?? undefined}>
                    <span>
                      <Button variant="contained" color="primaryLight" onClick={handleAddRule}>
                        {t('rules.create.button')}
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Button variant="contained" color="primaryLight" onClick={() => setDiscardOpen(true)}>
                  {t('rules.discard.button')}
                </Button>

                <Tooltip title={publishTooltip ?? undefined}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!canPublish}
                      onClick={() => setPublishOpen(true)}
                    >
                      {t('rules.publish.button')}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}

            {familyEditable && !isDraft && (
              <Tooltip title={t('rules.summary.startEditingTooltip') ?? undefined}>
                <span>
                  <LoadingButton loading={creatingDraft} variant="contained" color="primary" onClick={handleStartDraft}>
                    {t('rules.summary.startEditingButton')}
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Directly under the button that toggles it — rendered after the card, a 58-row table sat
        between the two and the panel opened far below the fold. */}
        {showHistory && (
          <Box sx={{ mb: 3 }}>
            <VersionHistory
              versions={versions}
              activeVersion={activeVersion}
              onEvaluate={version => openDryRun(version.id)}
              onRestored={refetchAll}
            />
          </Box>
        )}

        {!isLoadingRules && isEmpty ? (
          <RulesEmptyState family={family} editable={familyEditable} onImported={refetchAll} />
        ) : family === RuleFamily.colorCoding ? (
          activeVersionData && (
            <ColorRubricPanel
              version={activeVersionData}
              editable={!!isDraft && familyEditable}
              onChanged={refetchAll}
            />
          )
        ) : family === RuleFamily.matching ? (
          activeVersionData && (
            <MatchingRulesPanel
              version={activeVersionData}
              editable={!!isDraft && familyEditable}
              onChanged={refetchAll}
            />
          )
        ) : (
          <RuleList
            family={family}
            rules={rules}
            loading={isLoadingRules}
            editable={!!isDraft && familyEditable}
            onDeleted={refetchAll}
          />
        )}
      </Paper>

      {dryRunTargetVersionId && (
        <DryRunBench
          open
          onClose={() => setDryRunTargetVersionId(null)}
          family={family}
          ruleSetVersionId={dryRunTargetVersionId}
          versions={versions}
        />
      )}

      {publishOpen && activeVersion && isDraft && (
        <PublishDialog
          open
          onClose={() => setPublishOpen(false)}
          version={activeVersion}
          ruleCount={ruleCount}
          onPublished={handlePublished}
        />
      )}

      {discardOpen && activeVersion && isDraft && (
        <DiscardDraftDialog
          open
          onClose={() => setDiscardOpen(false)}
          version={activeVersion}
          parentVersionId={parentVersionId}
          ruleCount={ruleCount}
          onDiscarded={handleDiscarded}
        />
      )}

      <Loader isLoading={creatingDraft} />
    </Box>
  );
};
